"use client";

/**
 * Generic mobile bottom sheet: slides up from the bottom, dims the page,
 * locks background scroll, closes on backdrop tap / Escape / swipe-down.
 * The body scrolls; an optional `footer` stays pinned at the bottom (for
 * primary actions that should always be reachable). Animation + drag
 * pattern lifted from MobileMoreSheet so the motion matches app-wide.
 *
 * Render this only on mobile (gate the `open` prop) - it's a fixed
 * full-screen overlay with no desktop styling.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { prefersReducedMotion } from "@/lib/motion";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { useBackToClose } from "@/lib/use-back-to-close";
import { useT } from "@/lib/locale-context";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  /** The content starts at the very top and runs edge to edge (a photo
   *  bleeds under the handle, which then sits on it). */
  flush?: boolean;
  /** "overlay": opened from inside the full-screen listing drawer
   *  (z-[1200]), so it sits on the dialog layer above it (ConfirmDialog
   *  on desktop, the report sheet: z-[1300]). */
  layer?: "page" | "overlay";
  /** The close button's label (screen readers). */
  closeLabel?: string;
  children: React.ReactNode;
}

const ANIM_MS = 200;

export default function BottomSheet({ open, onClose, title, footer, flush = false, layer = "page", closeLabel, children }: Props) {
  const t = useT();
  // Hardware Back closes this overlay instead of leaving the page.
  useBackToClose(open, onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const { offset, dragging, handleProps, panelRef } = useSheetDrag(onClose, open);

  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open) { setRender(true); return; }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), ANIM_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => {
      // The closed position must be computed before the open one, or the
      // transition has nothing to start from: a WebView that mounts and
      // opens the sheet within one frame showed it in place, no slide
      // (Telegram, 2026-10-23). Reading layout here forces that first
      // computation.
      void document.body.offsetHeight;
      setEntered(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  if (!render) return null;
  const reduce = prefersReducedMotion();

  // PORTAL to <body>: consumers mount this sheet anywhere in the page
  // tree, and any transformed/filtered ancestor creates a stacking
  // context that traps our z-index below the root-level MobileTabBar
  // (z-1100) - the tab bar painted over the sheet body. Rendering at
  // the body level makes the z values compete in one context.
  return createPortal(
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ opacity: entered ? 1 : 0, transition: "opacity 200ms ease-out" }}
        className={`fixed inset-0 ${layer === "overlay" ? "z-[1300]" : "z-[1110]"} bg-black/30 backdrop-blur-[1px]`}
      />
      <div ref={panelRef}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: !entered ? (reduce ? "none" : "translateY(100%)") : `translateY(${offset}px)`,
          opacity: entered ? 1 : reduce ? 0 : 1,
          transition: dragging ? "none" : "transform 200ms ease-out, opacity 200ms ease-out",
        }}
        // Dark mode: the lifted card surface of .dark .bg-white (globals.css),
        // opaque, like the listing drawer; not the page's black (owner,
        // 2026-10-11: "I don't like how dark the sheet is").
        className={`fixed inset-x-0 bottom-0 ${layer === "overlay" ? "z-[1310]" : "z-[1120]"} flex max-h-[min(85dvh,calc(100dvh-3rem))] flex-col rounded-t-3xl bg-white shadow-2xl will-change-transform ${flush ? "overflow-hidden" : ""}`}
      >
        <div
          {...handleProps}
          role="button"
          tabIndex={0}
          aria-label={t("a11y.drag_close")}
          className={`flex shrink-0 cursor-grab touch-none justify-center active:cursor-grabbing ${
            flush ? "absolute inset-x-0 top-0 z-10 pt-2.5 pb-3" : "pt-3 pb-1"
          }`}
        >
          <div className={`h-1.5 w-10 rounded-full ${flush ? "bg-white/80 shadow-sm" : "bg-gray-300"}`} />
        </div>

        {title && (
          <div className="flex shrink-0 items-center justify-between px-4 pb-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
            {/* No visible X on a phone sheet (owner, 2026-10-23): it
                closes by a swipe down, a tap outside, Back or Escape, as
                Instagram's do; screen readers keep a close button. */}
            <button type="button" onClick={onClose} className="sr-only">
              {closeLabel ?? t("a11y.close")}
            </button>
          </div>
        )}

        {/* Without a footer the body itself clears the home indicator (owner,
            2026-10-10: the layer filters sat glued to the bottom). */}
        <div className={`min-h-0 flex-1 overflow-y-auto ${flush ? "" : "px-4"} ${footer ? "pb-3" : "pb-[calc(1.25rem+env(safe-area-inset-bottom))]"}`}>{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-gray-100 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
